/**
 * Admin Dashboard — Cluvik Control Plane
 *
 * Every number on this page is either read directly from an existing
 * Admin API response, or a client-side derivation clearly documented
 * inline (e.g. Monthly Revenue = Σ plan.price_monthly × plan.tenant_count
 * from GET /admin/plans). Nothing is fabricated. Where the redesign brief
 * asked for a historical trend (tenant growth, revenue trend, user growth,
 * storage growth, API usage heatmap) and no backend time-series endpoint
 * exists to back it, that specific chart was intentionally not built —
 * see the "Historical trend charts" note near the bottom of this page
 * instead of a wall of fake empty states.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts';
import { useAdminAuth } from './auth/AdminAuthContext';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import MetricCard from '../../components/admin/MetricCard';
import StatGrid from '../../components/admin/StatGrid';
import ChartContainer from '../../components/admin/ChartContainer';
import HealthCard from '../../components/admin/HealthCard';
import Timeline from '../../components/admin/Timeline';
import InsightCard from '../../components/admin/InsightCard';
import ActionToolbar from '../../components/admin/ActionToolbar';
import EmptyState from '../../components/admin/EmptyState';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Building2, Users, DollarSign, Zap, HardDrive, Package, Key, Activity,
  ArrowRight, RefreshCw, AlertTriangle, Plus, ScrollText, CreditCard,
  Rocket, Database, Server, Cpu, Workflow, Info, TrendingUp, Calendar,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_API = `${BACKEND_URL}/api/admin`;

const CHART_COLORS = ['hsl(var(--chart-1))', 'hsl(var(--chart-2))', 'hsl(var(--chart-3))', 'hsl(var(--chart-4))', 'hsl(var(--chart-5))', 'hsl(var(--muted-foreground))'];

const currency = (v) => `$${Math.round(v).toLocaleString()}`;
const timeAgo = (iso) => {
  if (!iso) return '';
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
};

const AdminDashboard = () => {
  const { getAdminToken } = useAdminAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [plans, setPlans] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [modules, setModules] = useState([]);
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const headers = { Authorization: `Bearer ${getAdminToken()}` };
    const [dashRes, plansRes, licensesRes, modulesRes, activityRes] = await Promise.allSettled([
      axios.get(`${ADMIN_API}/dashboard`, { headers }),
      axios.get(`${ADMIN_API}/plans`, { headers, params: { include_inactive: false } }),
      axios.get(`${ADMIN_API}/license-catalog`, { headers }),
      axios.get(`${ADMIN_API}/modules/available`, { headers }),
      axios.get(`${ADMIN_API}/activity/dashboard`, { headers }),
    ]);

    if (dashRes.status === 'fulfilled') {
      setStats(dashRes.value.data);
      setError(null);
    } else {
      setError(dashRes.reason?.response?.data?.detail || 'Failed to load dashboard data');
    }
    setPlans(plansRes.status === 'fulfilled' ? plansRes.value.data.plans || [] : []);
    setLicenses(licensesRes.status === 'fulfilled' ? licensesRes.value.data.licenses || [] : []);
    setModules(modulesRes.status === 'fulfilled' ? modulesRes.value.data.modules || [] : []);
    setActivity(activityRes.status === 'fulfilled' ? activityRes.value.data : null);
    setLastUpdated(new Date());
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // ---- Derivations (all from already-fetched real data) ----

  const totalTenants = stats?.total_tenants || 0;
  const newTenantsThisMonth = stats?.new_tenants_this_month || 0;
  const statusBreakdown = useMemo(() => stats?.tenant_status_breakdown || {}, [stats]);
  const activeTenantsCount = statusBreakdown.ACTIVE ?? statusBreakdown.active ?? 0;

  // Monthly Revenue: derived, not a real billed/collected figure -- see hint on the card.
  const activePlans = useMemo(() => plans.filter((p) => p.is_active !== false), [plans]);

  // "Storage Used" was removed from this dashboard (see the widget-audit
  // note near Platform Health below) -- total_storage_used_mb is not a
  // real storage measurement, it's `total_records * 0.001` computed in
  // get_dashboard_stats() (admin_service.py) because current_storage_mb is
  // never actually persisted anywhere. Replaced with genuinely real,
  // already-fetched data: Active Plans (from /admin/plans) and Active
  // Admins in the last 7 days (from /admin/activity/dashboard's
  // active_admins, previously fetched but unused on this page).
  const activeAdmins7d = activity?.active_admins || [];
  const monthlyRevenueEstimate = useMemo(
    () => activePlans.reduce((sum, p) => sum + (p.price_monthly || 0) * (p.tenant_count || 0), 0),
    [activePlans]
  );

  const sellableProducts = useMemo(
    () => modules.filter((m) => m.product_category === 'sellable' && !m.duplicate_of).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
    [modules]
  );
  const activeProductsCount = useMemo(
    () => sellableProducts.filter((m) => (stats?.module_usage?.[m.api_name] || 0) > 0).length,
    [sellableProducts, stats]
  );

  const activeLicensesCount = useMemo(() => licenses.filter((l) => l.is_active).length, [licenses]);

  const adminActions24h = activity?.summary_24h?.total_actions ?? null;

  const productAdoptionData = useMemo(
    () => sellableProducts.map((m) => ({
      name: m.name,
      tenants: stats?.module_usage?.[m.api_name] || 0,
    })),
    [sellableProducts, stats]
  );

  const subscriptionDistributionData = useMemo(
    () => activePlans
      .map((p) => ({ name: p.name || p.api_name, value: p.tenant_count || 0 }))
      .filter((p) => p.value > 0)
      .sort((a, b) => b.value - a.value),
    [activePlans]
  );

  const revenueByPlanData = useMemo(
    () => activePlans
      .map((p) => ({ name: p.name || p.api_name, revenue: (p.price_monthly || 0) * (p.tenant_count || 0) }))
      .filter((p) => p.revenue > 0)
      .sort((a, b) => b.revenue - a.revenue),
    [activePlans]
  );

  const tenantStatusData = useMemo(() => {
    const entries = Object.entries(statusBreakdown).filter(([, v]) => v > 0);
    return entries.map(([name, value]) => ({ name: name.replace(/_/g, ' '), value }));
  }, [statusBreakdown]);

  // Activity feed: merges two real sources -- recent admin logins
  // (GET /admin/activity/dashboard) and recent tenant creations
  // (GET /admin/dashboard's recent_tenants) -- sorted by time. No other
  // event types (subscription upgrades, webhook failures, releases...)
  // are backed by a queryable feed today, so they're not included rather
  // than invented.
  const activityItems = useMemo(() => {
    const items = [];
    (activity?.recent_logins || []).forEach((log, i) => {
      items.push({
        id: `login-${i}`,
        title: log.action === 'admin_login_failed'
          ? `Failed login attempt — ${log.actor_email || 'unknown'}`
          : `${log.actor_email || 'Admin'} signed in`,
        timestamp: log.timestamp,
        tone: log.action === 'admin_login_failed' ? 'destructive' : 'primary',
      });
    });
    (stats?.recent_tenants || []).forEach((t) => {
      items.push({
        id: `tenant-${t.id}`,
        title: `Tenant created — ${t.organization_name || t.tenant_name || t.company_name}`,
        meta: t.plan ? `Plan: ${t.plan}` : undefined,
        timestamp: t.created_at,
        tone: 'success',
      });
    });
    return items.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)).slice(0, 8);
  }, [activity, stats]);

  const quickActions = [
    { label: 'Create Tenant', description: 'Onboard a new organization', icon: Plus, onClick: () => navigate('/tenants/create') },
    { label: 'Create Plan', description: 'Define a subscription tier', icon: CreditCard, onClick: () => navigate('/subscriptions') },
    { label: 'Create License', description: 'Add to the license catalog', icon: Key, onClick: () => navigate('/license-catalog') },
    { label: 'Audit Logs', description: 'Review platform activity', icon: ScrollText, onClick: () => navigate('/audit-logs') },
    { label: 'Releases', description: 'Manage platform versions', icon: Rocket, onClick: () => navigate('/releases') },
  ];

  if (loading && !stats) {
    return (
      <div className="mx-auto max-w-[1600px] space-y-6 p-6">
        <Skeleton className="h-9 w-64" />
        <StatGrid cols={5}>
          {Array.from({ length: 5 }).map((_, i) => <MetricCard key={i} loading />)}
        </StatGrid>
        <div className="grid gap-4 lg:grid-cols-3">
          <Skeleton className="h-72 lg:col-span-2" />
          <Skeleton className="h-72" />
        </div>
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className="p-6">
        <EmptyState
          icon={AlertTriangle}
          title="Couldn't load the dashboard"
          description={error}
          actionLabel="Retry"
          onAction={fetchAll}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1600px] space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            Control Plane
          </h1>
          <p className="mt-0.5 text-sm text-muted-foreground">Platform-wide overview, live from the Admin API</p>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdated && (
            <span className="text-xs text-muted-foreground">
              Updated {timeAgo(lastUpdated.toISOString())}
            </span>
          )}
          <Button variant="outline" size="sm" onClick={fetchAll} className="gap-2">
            <RefreshCw className={loading ? 'h-3.5 w-3.5 animate-spin' : 'h-3.5 w-3.5'} />
            Refresh
          </Button>
        </div>
      </div>

      {/* KPI hero */}
      <StatGrid cols={5}>
        <MetricCard
          label="Total Tenants"
          value={totalTenants}
          icon={Building2}
          accent="primary"
          trend={newTenantsThisMonth > 0 ? { direction: 'up', value: newTenantsThisMonth, suffix: '', label: 'this month' } : { direction: 'flat', label: 'no new tenants this month' }}
        />
        <MetricCard label="Total Users" value={stats?.total_users || 0} icon={Users} accent="violet" />
        <MetricCard
          label="Monthly Revenue"
          value={monthlyRevenueEstimate}
          format={currency}
          icon={DollarSign}
          accent="success"
          hint="Estimated: Σ (plan monthly price × tenants on that plan), from GET /admin/plans. Not a confirmed Stripe-collected figure."
        />
        <MetricCard
          label="Active Tenants"
          value={activeTenantsCount}
          icon={Activity}
          accent="sky"
          hint="Tenants with lifecycle status ACTIVE (tenant_status_breakdown). Reflects account status, not confirmed billing subscription status."
        />
        <MetricCard
          label="Active Plans"
          value={activePlans.length}
          icon={CreditCard}
          accent="warning"
          hint="Plans with is_active: true, from GET /admin/plans. Replaces the old 'Storage Used' tile — see the note near Platform Health for why."
        />
        <MetricCard label="Active Workflows" value={stats?.active_flows || 0} icon={Zap} accent="primary"
          hint="Real, live count: flows collection documents with status 'Active' (db.flows.count_documents)."
          trend={{ direction: 'flat', value: stats?.total_flow_executions || 0, suffix: '', label: 'executions today' }}
        />
        <MetricCard label="Active Products" value={activeProductsCount} icon={Package} accent="violet"
          hint={`${activeProductsCount} of ${sellableProducts.length} sellable products have at least one tenant using them (module_usage).`}
        />
        <MetricCard label="Active Licenses" value={activeLicensesCount} icon={Key} accent="success"
          hint={`${activeLicensesCount} of ${licenses.length} License Catalog entries are marked active.`}
        />
        <MetricCard
          label="Admin Actions (24h)"
          value={adminActions24h ?? 0}
          icon={ScrollText}
          accent="sky"
          hint="Platform admin audit-log actions in the last 24 hours (not tenant-facing API request volume — no such metric is exposed today)."
        />
      </StatGrid>

      {/* Charts row 1 */}
      <div className="grid gap-4 lg:grid-cols-3">
        <ChartContainer
          title="Product Adoption"
          description="Tenants using each sellable product"
          className="lg:col-span-2"
          isEmpty={!productAdoptionData.some((d) => d.tenants > 0)}
          emptyProps={{ icon: Package, title: 'No product usage yet', description: 'Adoption will appear once tenants enable products.' }}
          height={Math.max(220, sellableProducts.length * 34)}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={productAdoptionData} layout="vertical" margin={{ left: 8, right: 16 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="hsl(var(--border))" />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 12, fill: 'hsl(var(--foreground))' }} axisLine={false} tickLine={false} />
              <RTooltip
                cursor={{ fill: 'hsl(var(--muted))' }}
                contentStyle={{ background: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }}
                formatter={(v) => [`${v} tenants`, '']}
              />
              <Bar dataKey="tenants" radius={[0, 6, 6, 0]} fill="hsl(var(--chart-1))" maxBarSize={18} isAnimationActive />
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>

        <ChartContainer
          title="Subscription Distribution"
          description="Tenants per active plan"
          isEmpty={subscriptionDistributionData.length === 0}
          emptyProps={{ icon: CreditCard, title: 'No plan assignments yet' }}
          height={200 + Math.max(1, Math.ceil(subscriptionDistributionData.length / 2)) * 24}
        >
          <div className="flex h-full flex-col">
            <div className="min-h-0 flex-1">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={subscriptionDistributionData}
                    dataKey="value"
                    nameKey="name"
                    innerRadius="50%"
                    outerRadius="78%"
                    paddingAngle={2}
                    isAnimationActive
                  >
                    {subscriptionDistributionData.map((entry, i) => (
                      <Cell key={entry.name} fill={CHART_COLORS[i % CHART_COLORS.length]} stroke="hsl(var(--card))" strokeWidth={2} />
                    ))}
                  </Pie>
                  <RTooltip contentStyle={{ background: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <ul className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1.5 px-1">
              {subscriptionDistributionData.map((entry, i) => (
                <li
                  key={entry.name}
                  className="flex min-w-0 items-center gap-1.5 text-[11px] leading-tight text-muted-foreground"
                >
                  <span
                    className="h-2 w-2 shrink-0 rounded-full"
                    style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }}
                    aria-hidden
                  />
                  <span className="truncate" title={`${entry.name} (${entry.value})`}>
                    {entry.name}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </ChartContainer>
      </div>

      {/* Charts row 2 */}
      <div className="grid gap-4 lg:grid-cols-3">
        <ChartContainer
          title="Revenue by Plan"
          description="Current monthly revenue (list price × active tenants)"
          isEmpty={revenueByPlanData.length === 0}
          emptyProps={{ icon: DollarSign, title: 'No billable plans yet' }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={revenueByPlanData} margin={{ left: 4, right: 8, top: 8 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v}`} />
              <RTooltip
                cursor={{ fill: 'hsl(var(--muted))' }}
                contentStyle={{ background: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }}
                formatter={(v) => [currency(v), 'Revenue']}
              />
              <Bar dataKey="revenue" radius={[6, 6, 0, 0]} fill="hsl(var(--chart-3))" maxBarSize={48} isAnimationActive />
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>

        <ChartContainer
          title="Tenant Status"
          description="Current lifecycle status breakdown"
          isEmpty={tenantStatusData.length === 0}
          emptyProps={{ icon: Building2, title: 'No tenants yet', actionLabel: 'Create Tenant', onAction: () => navigate('/tenants/create') }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={tenantStatusData} dataKey="value" nameKey="name" innerRadius="55%" outerRadius="85%" paddingAngle={2} isAnimationActive>
                {tenantStatusData.map((entry, i) => (
                  <Cell key={entry.name} fill={CHART_COLORS[i % CHART_COLORS.length]} stroke="hsl(var(--card))" strokeWidth={2} />
                ))}
              </Pie>
              <RTooltip contentStyle={{ background: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }} />
              <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartContainer>

        <Card className="flex flex-col p-5">
          <div>
            <p className="text-sm font-semibold text-foreground">Active Admins (7d)</p>
            <p className="mt-0.5 text-xs text-muted-foreground">Platform admins who logged in this week</p>
          </div>
          <div className="my-3 text-3xl font-semibold tabular-nums text-foreground">{activeAdmins7d.length}</div>
          {activeAdmins7d.length > 0 ? (
            <ul className="space-y-1.5 overflow-y-auto">
              {activeAdmins7d.slice(0, 4).map((a) => (
                <li key={a.email} className="flex items-center justify-between text-xs">
                  <span className="truncate text-foreground">{a.email}</span>
                  <span className="shrink-0 text-muted-foreground">{a.login_count_7d}x</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-muted-foreground">No admin logins in the last 7 days.</p>
          )}
        </Card>
      </div>

      {/* Historical trend note -- honest about what isn't buildable today */}
      <InsightCard
        icon={Info}
        tone="primary"
        title="Historical trend charts (Tenant Growth, Revenue Trend, User Growth, Storage Growth, API Usage) aren't shown"
        description="These need a time-series backend endpoint (daily/weekly snapshots) that doesn't exist yet. Rather than fabricate data points, the charts above use only current, real snapshot data from the existing Admin API. Adding a metrics-history endpoint would unlock these without any further frontend rework."
      />

      {/* Bottom row */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-semibold">Activity Feed</CardTitle>
            <Button variant="ghost" size="sm" className="h-7 text-xs text-primary" onClick={() => navigate('/audit-logs')}>
              View all <ArrowRight className="ml-1 h-3 w-3" />
            </Button>
          </CardHeader>
          <CardContent>
            <Timeline items={activityItems} renderTimestamp={timeAgo} />
          </CardContent>
        </Card>

        <Card className="lg:col-span-1">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-semibold">
              <Activity className="h-4 w-4 text-muted-foreground" /> Platform Health
            </CardTitle>
            <CardDescription className="text-xs">
              API &amp; Database reflect this page's own fetch; the rest have no monitoring signal exposed today.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-2.5">
            <HealthCard icon={Server} name="API" status={error ? 'down' : 'operational'} />
            <HealthCard icon={Database} name="Database" status={error ? 'down' : 'operational'} />
            <HealthCard icon={Cpu} name="Redis" status="unmonitored" />
            <HealthCard icon={HardDrive} name="Storage" status="unmonitored" />
            <HealthCard icon={Workflow} name="Workers" status="unmonitored" />
            <HealthCard icon={ScrollText} name="Job Queue" status="unmonitored" />
          </CardContent>
        </Card>

        <Card className="lg:col-span-1">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold">Recent Tenants</CardTitle>
              <Button variant="ghost" size="sm" className="h-7 text-xs text-primary" onClick={() => navigate('/tenants')}>
                View all <ArrowRight className="ml-1 h-3 w-3" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-1">
            {stats?.recent_tenants?.length > 0 ? (
              stats.recent_tenants.slice(0, 5).map((tenant) => (
                <button
                  key={tenant.id}
                  onClick={() => navigate(`/tenants/${tenant.id}`)}
                  className="flex w-full items-center justify-between gap-2 rounded-lg px-2 py-2 text-left transition-colors hover:bg-accent"
                >
                  <div className="flex min-w-0 items-center gap-2.5">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-chart-2 text-xs font-semibold text-primary-foreground">
                      {(tenant.organization_name || tenant.tenant_name || tenant.company_name || 'T')[0]}
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-foreground">
                        {tenant.organization_name || tenant.tenant_name || tenant.company_name}
                      </p>
                      <p className="flex items-center gap-1 text-[11px] text-muted-foreground">
                        <Calendar className="h-2.5 w-2.5" /> {new Date(tenant.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <Badge variant="outline" className="shrink-0 text-[10px] capitalize">{tenant.status || 'unknown'}</Badge>
                </button>
              ))
            ) : (
              <EmptyState compact icon={Building2} title="No tenants yet" actionLabel="Create Tenant" onAction={() => navigate('/tenants/create')} />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div>
        <div className="mb-3 flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-sm font-semibold text-foreground">Quick Actions</h2>
        </div>
        <ActionToolbar actions={quickActions} />
      </div>
    </div>
  );
};

export default AdminDashboard;
